import { useState, useEffect, useMemo } from 'react';
import { sessionsApi, enrollmentsApi, facilitadoresApi, type FacilitatorSession, type ProgramEnrollment, type Facilitador } from '../services/programService';

const TIPOS = [
  { value: 'diagnostico', label: 'Diagnóstico', color: 'bg-violet-500' },
  { value: 'consultoria', label: 'Consultoria', color: 'bg-amber-500' },
  { value: 'revisao', label: 'Revisão', color: 'bg-sky-500' },
  { value: 'encerramento', label: 'Encerramento', color: 'bg-emerald-500' },
];

const STATUS_SESSAO: Record<string, { label: string; color: string; bg: string; text: string }> = {
  agendada:   { label: 'Agendada',   color: 'border-blue-400',   bg: 'bg-blue-50',   text: 'text-blue-700' },
  realizada:   { label: 'Realizada',  color: 'border-green-400',  bg: 'bg-green-50',  text: 'text-green-700' },
  cancelada:   { label: 'Cancelada',  color: 'border-red-400',    bg: 'bg-red-50',    text: 'text-red-600' },
  remarcada:   { label: 'Remarcada',  color: 'border-yellow-400',  bg: 'bg-yellow-50', text: 'text-yellow-700' },
};

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function getMonthDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay();
  const days: { date: Date; inMonth: boolean }[] = [];

  // leading empty cells
  for (let i = 0; i < startDow; i++) {
    const d = new Date(year, month, -startDow + i + 1);
    days.push({ date: d, inMonth: false });
  }
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), inMonth: true });
  }
  // fill remaining cells to 42 (6 weeks)
  while (days.length < 42) {
    const d = new Date(year, month + 1, days.length - startDow - lastDay.getDate() + 1);
    days.push({ date: d, inMonth: false });
  }
  return days;
}

function formatDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getTipoColor(tipo: string) {
  return TIPOS.find(t => t.value === tipo)?.color ?? 'bg-gray-400';
}

export default function AgendaPage() {
  const [list, setList] = useState<FacilitatorSession[]>([]);
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [facilitadores, setFacilitadores] = useState<Facilitador[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const [form, setForm] = useState({
    enrollment_id: '', facilitador_id: '', tipo: 'consultoria', titulo: '',
    data_sessao: '', hora_sessao: '', duracao_min: 60, local: '', link_meet: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sessions, ens, facs] = await Promise.all([
        sessionsApi.list(),
        enrollmentsApi.list(),
        facilitadoresApi.list(),
      ]);
      setList(sessions);
      setEnrollments(ens);
      setFacilitadores(facs);
    } finally { setLoading(false); }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await sessionsApi.create(form as any);
      setShow(false);
      setForm({ enrollment_id: '', facilitador_id: '', tipo: 'consultoria', titulo: '', data_sessao: '', hora_sessao: '', duracao_min: 60, local: '', link_meet: '' });
      load();
    } finally { setSaving(false); }
  };

  const handleStatus = async (id: string, status: string) => {
    await sessionsApi.update(id, { status });
    load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir esta sessão?')) return;
    await sessionsApi.update(id, { status: 'cancelada' });
    load();
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthName = viewDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const days = useMemo(() => getMonthDays(year, month), [year, month]);

  const sessionsByDate = useMemo(() => {
    const map: Record<string, FacilitatorSession[]> = {};
    list.forEach(s => {
      const d = s.data_sessao;
      if (!map[d]) map[d] = [];
      map[d].push(s);
    });
    return map;
  }, [list]);

  const selectedDaySessions = selectedDay ? (sessionsByDate[selectedDay] ?? []) : [];

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToday = () => { setViewDate(new Date()); setSelectedDay(formatDate(new Date())); };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-black text-gray-900">Agenda de Sessões</h1>
        <button
          onClick={() => setShow(true)}
          className="bg-amber-500 hover:bg-amber-400 text-gray-950 font-bold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer flex items-center gap-2"
        >
          <i className="ri-add-line" /> Nova Sessão
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Carregando agenda...</div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Calendário */}
          <div className="flex-1 bg-white rounded-2xl border border-gray-200 overflow-hidden">
            {/* Header do calendário */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                <i className="ri-arrow-left-s-line text-gray-600 text-lg" />
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-black text-gray-900 capitalize">{monthName}</h2>
                <button onClick={goToday} className="text-xs bg-amber-100 hover:bg-amber-200 text-amber-700 font-bold px-2.5 py-1 rounded-lg transition-colors cursor-pointer">
                  Hoje
                </button>
              </div>
              <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors cursor-pointer">
                <i className="ri-arrow-right-s-line text-gray-600 text-lg" />
              </button>
            </div>

            {/* Grade do calendário */}
            <div className="p-3">
              {/* Dias da semana */}
              <div className="grid grid-cols-7 mb-1">
                {DIAS_SEMANA.map(d => (
                  <div key={d} className="text-center text-xs font-bold text-gray-400 py-2">{d}</div>
                ))}
              </div>

              {/* Células dos dias */}
              <div className="grid grid-cols-7 gap-1">
                {days.map((dayInfo, idx) => {
                  const dateStr = formatDate(dayInfo.date);
                  const isToday = dateStr === formatDate(new Date());
                  const isSelected = dateStr === selectedDay;
                  const sessions = sessionsByDate[dateStr] ?? [];
                  const hasSessions = sessions.length > 0;
                  const isCurrentMonth = dayInfo.inMonth;

                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedDay(dateStr)}
                      className={`
                        relative flex flex-col items-center rounded-xl py-2 px-1 min-h-[60px] transition-all cursor-pointer
                        ${isSelected ? 'bg-amber-50 border-2 border-amber-400' : isToday ? 'bg-indigo-50 border-2 border-indigo-300' : 'hover:bg-gray-50 border-2 border-transparent'}
                        ${!isCurrentMonth ? 'opacity-30' : ''}
                      `}
                    >
                      <span className={`text-xs font-bold mb-1 ${isSelected ? 'text-amber-700' : isToday ? 'text-indigo-700' : 'text-gray-600'}`}>
                        {dayInfo.date.getDate()}
                      </span>

                      {/* Indicadores de sessão */}
                      <div className="flex flex-wrap gap-0.5 justify-center mt-auto">
                        {sessions.slice(0, 3).map((s, i) => (
                          <span key={i} className={`w-2 h-2 rounded-full ${getTipoColor(s.tipo)}`} />
                        ))}
                        {sessions.length > 3 && (
                          <span className="text-[9px] font-bold text-gray-400">+{sessions.length - 3}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legenda */}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                {TIPOS.map(t => (
                  <div key={t.value} className="flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${t.color}`} />
                    <span className="text-xs text-gray-500 font-medium">{t.label}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="w-2.5 h-2.5 rounded-full bg-indigo-400" />
                  <span className="text-xs text-gray-500 font-medium">Hoje</span>
                </div>
              </div>
            </div>
          </div>

          {/* Painel do dia selecionado */}
          <div className="lg:w-80 bg-white rounded-2xl border border-gray-200 flex flex-col max-h-[600px]">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-shrink-0">
              <h3 className="text-sm font-black text-gray-900">
                {selectedDay
                  ? new Date(selectedDay + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
                  : 'Selecione um dia'}
              </h3>
              {selectedDay && (
                <span className="text-xs text-gray-400 font-semibold">{selectedDaySessions.length} sessão{selectedDaySessions.length !== 1 ? 'ões' : ''}</span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!selectedDay ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="ri-calendar-line text-3xl mb-2 block" />
                  <p className="text-sm">Clique em um dia para ver as sessões</p>
                </div>
              ) : selectedDaySessions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <i className="ri-calendar-event-line text-3xl mb-2 block" />
                  <p className="text-sm">Nenhuma sessão neste dia</p>
                </div>
              ) : (
                selectedDaySessions.map(s => {
                  const statusCfg = STATUS_SESSAO[s.status] ?? STATUS_SESSAO.agendada;
                  const enrollment = enrollments.find(e => e.id === s.enrollment_id);
                  const facilitador = facilitadores.find(f => f.id === s.facilitador_id);

                  return (
                    <div key={s.id} className={`rounded-xl border-l-4 ${statusCfg.color} ${statusCfg.bg} p-3`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-gray-900 truncate">{s.titulo || TIPOS.find(t => t.value === s.tipo)?.label}</h4>
                          {enrollment?.leads && (
                            <p className="text-xs text-gray-600 font-medium truncate">{enrollment.leads.nome}</p>
                          )}
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${statusCfg.bg} ${statusCfg.text}`}>
                          {statusCfg.label}
                        </span>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                        {s.hora_sessao && (
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line" /> {s.hora_sessao}
                          </span>
                        )}
                        {s.duracao_min && (
                          <span className="flex items-center gap-1">
                            <i className="ri-timer-line" /> {s.duracao_min}min
                          </span>
                        )}
                        {s.local && (
                          <span className="flex items-center gap-1 truncate max-w-[100px]">
                            <i className="ri-map-pin-line" /> {s.local}
                          </span>
                        )}
                      </div>

                      {facilitador && (
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: facilitador.cor_avatar || '#6366f1' }}>
                            {facilitador.nome.charAt(0)}
                          </div>
                          <span className="text-xs text-gray-600 font-medium">{facilitador.nome}</span>
                        </div>
                      )}

                      {s.link_meet && (
                        <a href={s.link_meet} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600 font-semibold mb-2">
                          <i className="ri-video-line" /> Abrir Meet
                        </a>
                      )}

                      {/* Ações de status */}
                      <div className="flex gap-1.5 pt-2 border-t border-gray-200/50 flex-wrap">
                        {Object.entries(STATUS_SESSAO)
                          .filter(([st]) => st !== s.status)
                          .map(([st, cfg]) => (
                            <button
                              key={st}
                              onClick={() => handleStatus(s.id, st)}
                              className={`text-[10px] font-bold px-2 py-1 rounded-lg ${cfg.bg} ${cfg.text} transition-colors cursor-pointer hover:opacity-80`}
                            >
                              {cfg.label}
                            </button>
                          ))}
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="text-[10px] font-bold px-2 py-1 rounded-lg bg-red-50 text-red-600 transition-colors cursor-pointer hover:opacity-80 ml-auto"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova Sessão */}
      {show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && setShow(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-gray-900">Nova Sessão</h2>
              <button onClick={() => setShow(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-gray-500 text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <select
                required
                value={form.enrollment_id}
                onChange={e => setForm({ ...form, enrollment_id: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
              >
                <option value="">Selecione o cliente</option>
                {enrollments.map(en => (
                  <option key={en.id} value={en.id}>
                    {en.leads?.nome ?? en.lead_id}
                    {en.leads?.empresa ? ` — ${en.leads.empresa}` : ''}
                  </option>
                ))}
              </select>

              <select
                required
                value={form.facilitador_id}
                onChange={e => setForm({ ...form, facilitador_id: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
              >
                <option value="">Selecione o facilitador</option>
                {facilitadores.map(f => (
                  <option key={f.id} value={f.id}>{f.nome}</option>
                ))}
              </select>

              <select
                value={form.tipo}
                onChange={e => setForm({ ...form, tipo: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
              >
                {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <input
                required
                placeholder="Título da sessão"
                value={form.titulo}
                onChange={e => setForm({ ...form, titulo: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
              />

              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  required
                  value={form.data_sessao}
                  onChange={e => setForm({ ...form, data_sessao: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
                />
                <input
                  type="time"
                  value={form.hora_sessao}
                  onChange={e => setForm({ ...form, hora_sessao: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <input
                  placeholder="Local / Cidade"
                  value={form.local}
                  onChange={e => setForm({ ...form, local: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
                />
                <input
                  placeholder="Link Meet / Zoom"
                  value={form.link_meet}
                  onChange={e => setForm({ ...form, link_meet: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShow(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-xl font-semibold text-sm cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-950 font-bold py-2.5 rounded-xl text-sm cursor-pointer flex items-center justify-center gap-2"
                >
                  {saving ? <><i className="ri-loader-4-line animate-spin" /> Salvando...</> : 'Agendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}